import { Controller } from "stimulus";
import { Calendar } from "vanilla-calendar-pro";

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default class extends Controller {
    static targets = ["mount", "checkin", "checkout", "selectBtn"];

    connect() {
        const minDate = this.element.dataset.minDate || today();
        const maxDate = this.element.dataset.maxDate || "";
        const enableDates = this._parseEnableDates();
        this.originalEnableDates = enableDates;
        this.minNights = parseInt(this.element.dataset.minNights, 10) || 0;
        this.maxNights = parseInt(this.element.dataset.maxNights, 10) || 0;
        this.dateMin = minDate;
        this.dateMax = maxDate || "";
        const initialCheckin = this.hasCheckinTarget ? this.checkinTarget.value : "";
        const initialCheckout = this.hasCheckoutTarget ? this.checkoutTarget.value : "";
        const initialDates = initialCheckin && initialCheckout ? [initialCheckin, initialCheckout] : [];

        const monthsToShow = this._monthsToShow();
        const initialMonth = this._initialMonth(initialDates, minDate);
        this.calendar = new Calendar(this.mountTarget, {
            type: monthsToShow > 1 ? "multiple" : "default",
            displayMonthsCount: monthsToShow,
            monthsToSwitch: 1,
            firstWeekday: 0,
            dateMin: minDate,
            ...(maxDate ? { dateMax: maxDate } : {}),
            ...(enableDates ? { disableAllDates: true, enableDates } : {}),
            ...(initialMonth ? { selectedMonth: initialMonth.month, selectedYear: initialMonth.year } : {}),
            displayDatesOutside: false,
            disableDatesPast: true,
            selectionDatesMode: "multiple-ranged",
            enableEdgeDatesOnly: false,
            enableJumpToSelectedDate: true,
            selectedDates: initialDates,
            styles: {
                mainElement: "vcp-villatel"
            },
            onUpdate: () => {
                if (this.activeCheckin) {
                    this._applyCheckoutWindowDOM();
                    this._showCheckinPopover();
                }
            },
            onClickDate: (self) => {
                const dates = (self.context.selectedDates || []).slice().sort();
                const ci = dates[0] || "";
                const co = dates[dates.length - 1] || "";
                const isPickingCheckout = dates.length === 1;

                if (this._hasNightConstraints()) {
                    if (isPickingCheckout) {
                        this._restrictToCheckoutWindow(ci);
                    } else {
                        this._resetDateRestrictions();
                    }
                }

                if (this.hasCheckinTarget) this.checkinTarget.value = ci;
                if (this.hasCheckoutTarget) this.checkoutTarget.value = co;

                if (ci && co && ci !== co && this._isStayValid(ci, co)) {
                    this._enableSelectBtn();
                } else {
                    this._disableSelectBtn();
                }

                this._dispatch("change", { checkin: ci, checkout: co });
            }
        });

        this.calendar.init();

        if (initialDates.length === 2 && this._isStayValid(initialDates[0], initialDates[1])) {
            this._enableSelectBtn();
        }

        this._onResize = this._onResize.bind(this);
        window.addEventListener("resize", this._onResize);
    }

    disconnect() {
        window.removeEventListener("resize", this._onResize);
        if (this.calendar && typeof this.calendar.destroy === "function") {
            this.calendar.destroy();
        }
    }

    prev() {
        if (!this.calendar) return;
        const ctx = this.calendar.context;
        let month = ctx.selectedMonth - 1;
        let year = ctx.selectedYear;
        if (month < 0) { month = 11; year -= 1; }
        this.calendar.set({ selectedMonth: month, selectedYear: year });
    }

    next() {
        if (!this.calendar) return;
        const ctx = this.calendar.context;
        let month = ctx.selectedMonth + 1;
        let year = ctx.selectedYear;
        if (month > 11) { month = 0; year += 1; }
        this.calendar.set({ selectedMonth: month, selectedYear: year });
    }

    clear() {
        if (this.hasCheckinTarget) this.checkinTarget.value = "";
        if (this.hasCheckoutTarget) this.checkoutTarget.value = "";
        if (this.calendar) {
            this.calendar.set({ selectedDates: [] });
            if (this._hasNightConstraints()) this._resetDateRestrictions();
        }
        this._disableSelectBtn();
        this._dispatch("clear", {});
    }

    emitSelect() {
        if (!this.hasCheckinTarget || !this.hasCheckoutTarget) return;
        const checkin = this.checkinTarget.value;
        const checkout = this.checkoutTarget.value;
        if (!checkin || !checkout) return;
        this._dispatch("select", { checkin, checkout });
    }

    _enableSelectBtn() {
        if (this.hasSelectBtnTarget) this.selectBtnTarget.disabled = false;
    }

    _disableSelectBtn() {
        if (this.hasSelectBtnTarget) this.selectBtnTarget.disabled = true;
    }

    _dispatch(name, detail) {
        const event = new CustomEvent(`standalone-calendar:${name}`, {
            bubbles: true,
            detail
        });
        this.element.dispatchEvent(event);
    }

    _monthsToShow() {
        return window.matchMedia("(min-width: 700px)").matches ? 2 : 1;
    }

    _isStayValid(ci, co) {
        const nights = this._daysBetween(ci, co);
        if (nights <= 0) return false;
        if (this.minNights && nights < this.minNights) return false;
        if (this.maxNights && nights > this.maxNights) return false;
        return true;
    }

    _hasNightConstraints() {
        return this.minNights > 0 || this.maxNights > 0;
    }

    _restrictToCheckoutWindow(ci) {
        if (!ci) return;
        this.activeCheckin = ci;
        this._applyCheckoutWindowDOM();
        this._showCheckinPopover();
    }

    _resetDateRestrictions() {
        this.activeCheckin = null;
        const buttons = this.mountTarget.querySelectorAll("[data-vc-date-btn]");
        buttons.forEach((btn) => {
            if (btn.dataset.vcCalendarBlocked === "1") {
                btn.disabled = false;
                delete btn.dataset.vcCalendarBlocked;
            }
        });
        this._hideCheckinPopover();
    }

    _showCheckinPopover() {
        this._hideCheckinPopover();
        if (!this.activeCheckin) return;
        const text = this._popoverText();
        if (!text) return;
        const cell = this.mountTarget.querySelector(`[data-vc-date="${this.activeCheckin}"]`);
        if (!cell) return;
        const popover = document.createElement("div");
        popover.className = "standalone-calendar__popover";
        popover.dataset.role = "standalone-calendar-popover";
        popover.textContent = text;
        cell.appendChild(popover);
    }

    _hideCheckinPopover() {
        this.mountTarget
            .querySelectorAll('[data-role="standalone-calendar-popover"]')
            .forEach((el) => el.remove());
    }

    _popoverText() {
        if (this.minNights > 0) return `Min ${this.minNights} ${this.minNights === 1 ? "night" : "nights"} required`;
        if (this.maxNights > 0) return `Max ${this.maxNights} ${this.maxNights === 1 ? "night" : "nights"}`;
        return "";
    }

    _applyCheckoutWindowDOM() {
        const ci = this.activeCheckin;
        if (!ci) return;
        const minCheckout = this.minNights ? this._addDays(ci, this.minNights) : null;
        const maxCheckout = this.maxNights ? this._addDays(ci, this.maxNights) : null;

        const dateEls = this.mountTarget.querySelectorAll("[data-vc-date]");
        dateEls.forEach((dateEl) => {
            const dateStr = dateEl.getAttribute("data-vc-date");
            if (!dateStr || dateStr === ci) return;
            const btn = dateEl.querySelector("[data-vc-date-btn]");
            if (!btn || btn.dataset.alreadyDisabled === "1") return;

            const blockedByMin = minCheckout && dateStr > ci && dateStr < minCheckout;
            const blockedByMax = maxCheckout && dateStr > maxCheckout;
            if (blockedByMin || blockedByMax) {
                if (!btn.disabled) {
                    btn.disabled = true;
                    btn.dataset.vcCalendarBlocked = "1";
                }
            }
        });
    }

    _daysBetween(a, b) {
        const da = new Date(`${a}T00:00:00`);
        const db = new Date(`${b}T00:00:00`);
        return Math.round((db - da) / 86400000);
    }

    _addDays(dateStr, n) {
        const d = new Date(`${dateStr}T00:00:00`);
        d.setDate(d.getDate() + n);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }

    _initialMonth(initialDates, minDate) {
        if (initialDates && initialDates.length) return null;
        if (!minDate) return null;
        const [y, m] = minDate.split("-").map((n) => parseInt(n, 10));
        if (!y || !m) return null;
        const now = new Date();
        const minIsFuture =
            y > now.getFullYear() ||
            (y === now.getFullYear() && m - 1 > now.getMonth());
        if (!minIsFuture) return null;
        return { month: m - 1, year: y };
    }

    _parseEnableDates() {
        const raw = this.element.dataset.enableDates;
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) && parsed.length ? parsed : null;
        } catch (e) {
            return null;
        }
    }

    _onResize() {
        if (!this.calendar) return;
        const target = this._monthsToShow();
        if (this.calendar.displayMonthsCount === target) return;
        this.calendar.set({
            type: target > 1 ? "multiple" : "default",
            displayMonthsCount: target
        });
    }
}
